import React from 'react';
import { Card, Form, Row, Col, Alert, Badge, Button } from 'react-bootstrap';
import moment from 'moment';
import {
  CERTIFICATE_TYPES,
  CERTIFICATE_TYPE_LABELS
} from '../../utils/constants';
import FileViewer from '../Common/FileViewer';

export const isCertValid = (cert) => {
  if (!cert) return false;
  const status = (cert.status || '').toLowerCase();
  if (status === 'expired') return false;
  if (cert.expiryDate) {
    const exp = new Date(cert.expiryDate);
    return exp > new Date();
  }
  return true;
};

export const getCertificateStatus = (cert) => {
  if (!cert) return { label: 'Non presente', variant: 'danger' };
  const status = (cert.status || '').toLowerCase();
  if (status === 'expired') return { label: 'Scaduto', variant: 'danger' };
  if (status === 'expiring') return { label: 'In scadenza', variant: 'warning' };
  return { label: 'Valido', variant: 'success' };
};

export const getCertificateTypeLabel = (type) => {
  if (type === CERTIFICATE_TYPES.COMPETITIVE || type === 'competitive' || type === 'Agonistico') return 'Agonistico';
  if (type === CERTIFICATE_TYPES.NON_COMPETITIVE || type === 'non_competitive' || type === 'Non Agonistico') return 'Non Agonistico';
  return CERTIFICATE_TYPE_LABELS[type] || type || '-';
};

const AthleteMedicalCertSection = ({
  isRegistration = false,
  currentCertificate = null,
  isRenewingCertificate = false,
  setIsRenewingCertificate,
  certForm,
  setCertForm,
  certFile,
  setCertFile,
  onFileValidationError,
  onFileDeleted
}) => {
  const handleIssueDateChange = (e) => {
    const issueDate = e.target.value;
    setCertForm(prev => {
      let expiryDate = prev.expiryDate;
      if (issueDate) {
        const d = new Date(issueDate);
        d.setFullYear(d.getFullYear() + 1);
        expiryDate = d.toISOString().split('T')[0];
      }
      return {
        ...prev,
        issueDate,
        expiryDate
      };
    });
  };

  const validateFile = (selectedFile) => {
    if (selectedFile.size > 10 * 1024 * 1024) {
      return 'Il file è troppo grande. Dimensione massima: 10MB';
    }
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(selectedFile.type)) {
      return 'Tipo di file non supportato. Usa PDF, JPG o PNG.';
    }
    return null;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      setCertFile(null);
      return;
    }
    const validationError = validateFile(selectedFile);
    if (validationError) {
      if (onFileValidationError) onFileValidationError(validationError);
      setCertFile(null);
      e.target.value = '';
      return;
    }
    setCertFile(selectedFile);
    if (onFileValidationError) onFileValidationError('');
  };

  const showExistingCert = !isRegistration && currentCertificate && isCertValid(currentCertificate) && !isRenewingCertificate;

  return (
    <Card className="mb-4 shadow-sm border-0" id="portal-athlete-medical-cert-section">
      <Card.Header className="bg-primary text-white py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h5 className="mb-0 text-white">
          <i className="fas fa-file-medical me-2"></i>
          Certificato Medico
        </h5>
        {!isRegistration && currentCertificate && isCertValid(currentCertificate) && (
          <Badge bg={getCertificateStatus(currentCertificate).variant} className="py-2 px-3">
            <i className="fas fa-check-circle me-1"></i>
            Certificato {getCertificateStatus(currentCertificate).label} (Scadenza: {moment(currentCertificate.expiryDate).format('DD/MM/YYYY')})
          </Badge>
        )}
        {!isRegistration && (!currentCertificate || !isCertValid(currentCertificate)) && (
          <Badge bg="danger" className="py-2 px-3">
            <i className="fas fa-exclamation-triangle me-1"></i>
            {currentCertificate && currentCertificate.status === 'expired' ? 'Certificato Scaduto' : 'Certificato Mancante'}
          </Badge>
        )}
      </Card.Header>
      <Card.Body>
        {showExistingCert ? (
          <div>
            <Row className="mb-3">
              <Col md={4} className="mb-2">
                <small className="text-muted d-block">Tipo Certificato</small>
                <strong className="fs-6">{getCertificateTypeLabel(currentCertificate.certificateType)}</strong>
              </Col>
              <Col md={4} className="mb-2">
                <small className="text-muted d-block">Data Rilascio</small>
                <span className="fs-6">{moment(currentCertificate.issueDate).format('DD/MM/YYYY')}</span>
              </Col>
              <Col md={4} className="mb-2">
                <small className="text-muted d-block">Data Scadenza</small>
                <span className={`fs-6 ${currentCertificate.status === 'expiring' ? 'text-warning fw-bold' : ''}`}>
                  {moment(currentCertificate.expiryDate).format('DD/MM/YYYY')}
                </span>
              </Col>
            </Row>

            <div className="mb-3">
              <small className="text-muted d-block mb-1">Documento Allegato</small>
              <FileViewer
                certificate={currentCertificate}
                type="certificate"
                onDeleteFile={onFileDeleted}
              />
            </div>

            <div className="d-flex justify-content-end mt-3 border-top pt-3">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  if (setIsRenewingCertificate) setIsRenewingCertificate(true);
                  setCertForm({
                    certificateType: '',
                    issueDate: '',
                    expiryDate: ''
                  });
                  setCertFile(null);
                }}
              >
                <i className="fas fa-sync-alt me-1"></i>
                Carica nuovo certificato / Rinnova
              </Button>
            </div>
          </div>
        ) : (
          <div>
            {!isRegistration && currentCertificate && !isCertValid(currentCertificate) && (
              <Alert variant="warning" className="py-2 small mb-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Il tuo certificato medico risulta scaduto o non valido. Puoi inserire i dati e caricare un nuovo certificato medico valido.
              </Alert>
            )}
            {!isRegistration && isRenewingCertificate && currentCertificate && (
              <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-light rounded border">
                <span className="small text-muted">
                  <i className="fas fa-info-circle me-1 text-primary"></i>
                  Stai registrando un nuovo certificato in sostituzione del precedente (scadenza {moment(currentCertificate.expiryDate).format('DD/MM/YYYY')}).
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    if (setIsRenewingCertificate) setIsRenewingCertificate(false);
                    setCertFile(null);
                    setCertForm({
                      certificateType: '',
                      issueDate: '',
                      expiryDate: ''
                    });
                  }}
                >
                  Annulla rinnovo
                </Button>
              </div>
            )}

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3" controlId="portal-cert-type">
                  <Form.Label>Tipo Certificato</Form.Label>
                  <Form.Select
                    name="certificateType"
                    value={certForm.certificateType || ''}
                    onChange={(e) => setCertForm(prev => ({ ...prev, certificateType: e.target.value }))}
                  >
                    <option value="">-- Seleziona --</option>
                    {Object.entries(CERTIFICATE_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3" controlId="portal-cert-issue-date">
                  <Form.Label>Data Rilascio</Form.Label>
                  <Form.Control
                    type="date"
                    name="issueDate"
                    value={certForm.issueDate}
                    onChange={handleIssueDateChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3" controlId="portal-cert-expiry-date">
                  <Form.Label>Data Scadenza</Form.Label>
                  <Form.Control
                    type="date"
                    name="expiryDate"
                    value={certForm.expiryDate}
                    onChange={(e) => setCertForm(prev => ({ ...prev, expiryDate: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="portal-cert-file">
              <Form.Label>Carica Documento (Opzionale)</Form.Label>
              <Form.Control
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.jpg,.jpeg,.png"
              />
              <Form.Text className="text-muted">
                Formati supportati: PDF, JPG, PNG. Dimensione massima: 10MB.
              </Form.Text>
              {certFile && (
                <div className="mt-2">
                  <small className="text-success">
                    <i className="fas fa-check-circle me-1"></i>
                    File selezionato: {certFile.name} ({(certFile.size / 1024 / 1024).toFixed(2)} MB)
                  </small>
                </div>
              )}
            </Form.Group>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default AthleteMedicalCertSection;
